import { MRT_ColumnDef } from "material-react-table"
import { useMemo } from "react"
import { Button, Chip } from "@mui/material"
import dayjs from "dayjs"
import { Visibility } from "@mui/icons-material"

import {
  cellNotFilter,
  cellEstadoReporteVentaCorporativa,
  formatDate,
  cellUbicacionEvaVentaCorporativa,
  EstadoFactibilidadCorporativaChoices,
  EstadoFactibilidadCorporativaENUM
} from "@/shared"
import { IReporteVentaCorporativa } from "@/shared/interfaces"
import { EstadoComponente } from "@/shared/components/ui"
import { useInfoCoordenadasStore } from "@/shared/components/ui/InfoCoordendas/useInfoCoordendas.store"
import InfoUser from "@/shared/components/ui/InfoUser/InfoUser"

interface UseColumnsSeguimientoCosteProps {
  setOpen?: (open: boolean) => void;
  setDocumentsArray?: (docs: any) => void;
}

const fnEstadosColor = (estado: EstadoFactibilidadCorporativaENUM) => {
  switch (estado) {
    case EstadoFactibilidadCorporativaENUM.APROBADO:
      return "green"
    case EstadoFactibilidadCorporativaENUM.CANCELADO:
      return "red"
    case EstadoFactibilidadCorporativaENUM.PENDIENTE:
      return "#007bff"
    case EstadoFactibilidadCorporativaENUM.EN_GESTION:
      return "orange"
    case EstadoFactibilidadCorporativaENUM.EN_REVISION:
      return "gray"
    default:
      return "black"
  }
}

export const useColumnsSeguimientoCoste = ({
  setOpen,
  setDocumentsArray
}: UseColumnsSeguimientoCosteProps = {}) => {
  const infoCoordendas = useInfoCoordenadasStore()

  const columnsSeguimientoCoste = useMemo<MRT_ColumnDef<IReporteVentaCorporativa>[]>(() => [
    // Columnas de Estado de Venta
    {
      accessorKey: 'razon_social',
      header: 'Razón Social',
      size: 300,
      Cell: ({ row }) => row.original?.razon_social || "N/A"
    },
    {
      accessorKey: 'identificacion',
      header: 'Identificación',
      Cell: ({ row }) => row.original?.identificacion || "N/A"
    },
    {
      accessorKey: 'nombre_plan',
      header: 'Nombre Enlace',
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const factibilidad = row.original?.factibilidad_corporativa_data?.[0];
        const nombreBase = row.original?.preventa_corporativa_data?.[0]?.data_plan_personalizado?.nombre_personalizado;
        if (!nombreBase) return "N/A";
        return factibilidad?.es_backup ? `${nombreBase}_BACKUP` : nombreBase;
      }
    },
    {
      accessorKey: 'tipo_plan',
      header: 'Subtipo de Contrato',
      enableColumnFilter: false,
      Cell: ({ row }) => row.original?.preventa_corporativa_data?.[0]?.tipo_plan ||
        row.original?.preventa_corporativa_data?.[0]?.data_plan_personalizado?.tipo_plan_personalizado || "N/A"
    },
    // Columnas de Solución Costeada (nuevas)
    {
      accessorKey: 'numero_seguimiento',
      header: 'Número de Seguimiento',
      Cell: ({ row }) => row.original?.preventa_corporativa_data?.[0]?.numero_seguimiento || "N/A",
      enableColumnFilter: true,
    },
    {
      accessorKey: 'estado_factibilidad',
      header: 'Estado Factibilidad',
      Cell: ({ row }) => {
        const factibilidad = row.original?.factibilidad_corporativa_data?.[0];
        if (!factibilidad?.estado_factibilidad) return null;

        return (
          <>
            <EstadoComponente
              backgroundColor={fnEstadosColor(factibilidad.estado_factibilidad as EstadoFactibilidadCorporativaENUM)}
              texto={factibilidad.estado_factibilidad || ''}
            />
            {factibilidad.estado_factibilidad === EstadoFactibilidadCorporativaENUM.EN_REVISION
              && factibilidad?.revisado_supervisor && !factibilidad?.aprobado_supervisor
              &&
              <div style={{width:"100%", justifyContent:"center", alignItems:"center", display:"flex"}}>
                <span style={{color:"black", fontSize:"10px", textAlign:"center"}}>
                  Carga de documentos solicitada
                </span>
              </div>
            }
            {factibilidad.estado_factibilidad === EstadoFactibilidadCorporativaENUM.EN_REVISION
              && factibilidad?.revisado_supervisor && factibilidad?.aprobado_supervisor
              &&
              <div style={{width:"100%", justifyContent:"center", alignItems:"center", display:"flex"}}>
                <span style={{color:"black", fontSize:"12px", textAlign:"center"}}>
                  En aprobación final
                </span>
              </div>
            }
          </>
        );
      },
      filterVariant: 'select',
      filterSelectOptions: EstadoFactibilidadCorporativaChoices,
      enableSorting: false,
    },
    // Columnas de Estado de Venta (ubicación y proceso)
    {
      ...cellNotFilter,
      accessorKey: 'ubicacion_venta',
      header: 'Ubicación Venta',
      Cell: ({ row }) => (
        <EstadoComponente backgroundColor={'blue'} texto={cellUbicacionEvaVentaCorporativa(row.original).ubicacion || ''} />
      )
    },
    {
      ...cellNotFilter,
      accessorKey: 'estado_venta',
      header: 'Proceso Actual Venta',
      Cell: ({ row }) => (
        <EstadoComponente backgroundColor={'green'} texto={cellEstadoReporteVentaCorporativa(row.original).modulo || ''} />
      )
    },
    {
      ...cellNotFilter,
      accessorKey: 'ultimo_estado',
      header: 'Estado Actual Venta',
      Cell: ({ row }) => (
        <EstadoComponente backgroundColor={'blue'} texto={cellEstadoReporteVentaCorporativa(row.original).ultimo_estado || ''} />
      )
    },
    {
      ...cellNotFilter,
      accessorKey: 'accion',
      header: 'Última Acción',
      Cell: ({ row }) => row.original?.trazo_corporativo_data?.[(row.original?.trazo_corporativo_data?.length||1)-1]?.accion || "N/A"
    },
    // Estado Comercial del Servicio (nuevo según requisito)
    {
      ...cellNotFilter,
      accessorKey: 'condicion_venta',
      header: 'Estado Comercial',
      Cell: ({ row }) => {
        const condicion = row.original?.condicion_venta;
        if (!condicion) return "N/A";
        return <EstadoComponente backgroundColor={'purple'} texto={condicion} />;
      }
    },
    // Columnas de Solución Costeada (canal y área)
    {
      accessorKey: 'canal_venta_create',
      header: 'Canal de Venta',
      enableColumnFilter: false,
      Cell: ({ row }) => row.original?.canal_venta_create || "N/A"
    },
    {
      accessorKey: 'area_create',
      header: 'Área',
      enableColumnFilter: false,
      Cell: ({ row }) => row.original?.area_create || "N/A"
    },
    // Equipos Aprobados (nuevo según requisito)
    {
      ...cellNotFilter,
      accessorKey: 'equipos_aprobados',
      header: 'Equipos Aprobados',
      Cell: ({ row }) => {
        const factibilidad = row.original?.factibilidad_corporativa_data?.[0];
        if (!factibilidad?.productos_costeados) return "N/A";

        const equipos = factibilidad.productos_costeados.filter(
          (p: any) => p.categoria_fcorporativa === 'MATERIALESEQUIPOS' ||
                      p.categoria_fcorporativa === 'EQUIPOSADICIONALES'
        );

        if (equipos.length === 0) return "Sin equipos";

        return (
          <Chip
            label={`${equipos.length} equipo(s)`}
            color="primary"
            size="small"
            variant="outlined"
          />
        );
      }
    },
    // Documentos Habilitantes
    {
      accessorKey: 'url_anexo_1',
      header: 'Docs. Habilitantes',
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const factibilidad = row.original?.factibilidad_corporativa_data?.[0];
        if (!factibilidad) return null;

        if (factibilidad?.documentos?.documentos_habilitantes?.length > 0) {
          return (
            <Button
              onClick={() => {
                setOpen?.(true);
                setDocumentsArray?.({documentos_habilitantes: factibilidad?.documentos?.documentos_habilitantes});
              }}
              variant="outlined"
              color="primary"
              size="small"
            >
              Ver Docs
            </Button>
          );
        }

        if (factibilidad?.url_anexo_1) {
          return (
            <Button
              href={factibilidad?.url_anexo_1}
              target="_blank"
              variant="outlined"
              color="primary"
              size="small"
            >
              Ver Doc
            </Button>
          );
        }

        return <Button disabled variant="outlined" color="primary" size="small">Sin Docs</Button>;
      }
    },
    // Documentos Contractuales
    {
      accessorKey: 'url_anexo_2',
      header: 'Docs. Contractuales',
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const factibilidad = row.original?.factibilidad_corporativa_data?.[0];
        if (!factibilidad) return null;

        if (factibilidad?.documentos?.documentos_contractuales?.length > 0) {
          return (
            <Button
              onClick={() => {
                setOpen?.(true);
                setDocumentsArray?.({documentos_contractuales: factibilidad?.documentos?.documentos_contractuales});
              }}
              variant="outlined"
              color="secondary"
              size="small"
            >
              Ver Docs
            </Button>
          );
        }

        if (factibilidad?.url_anexo_2) {
          return (
            <Button
              href={factibilidad?.url_anexo_2}
              target="_blank"
              variant="outlined"
              color="secondary"
              size="small"
            >
              Ver Doc
            </Button>
          );
        }

        return <Button disabled variant="outlined" color="primary" size="small">Sin Docs</Button>;
      }
    },
    // Coordenada servicio
    {
      accessorKey: 'coordenada_servicio',
      enableColumnFilter: false,
      header: 'Coordenada Servicio',
      Cell: ({ row }) =>
        row.original?.preventa_corporativa_data?.[0] ?
        <Button
          endIcon={<Visibility />}
          size="small"
          onClick={() => {
            infoCoordendas.handlePostInfoZone({
              latitud: row.original?.preventa_corporativa_data?.[0]?.coordenada_servicio?.split(',').at(0)!,
              longitud: row.original?.preventa_corporativa_data?.[0]?.coordenada_servicio?.split(',').at(1)!,
            }, row.original?.preventa_corporativa_data?.[0]?.sector ?? '0,0')
          }}
        >
          {row.original?.preventa_corporativa_data?.[0]?.coordenada_servicio}
        </Button>
        : "N/A"
    },
    // Asesor Venta
    {
      accessorKey: 'user_create',
      header: 'Asesor Venta',
      Cell: ({ row }) => (
        <InfoUser user={row?.original?.user_create!}/>
      )
    },
    // Fechas
    {
      ...cellNotFilter,
      accessorKey: 'fecha_registro',
      header: 'Fecha de Registro',
      Cell: ({ row }) => (
        <EstadoComponente backgroundColor={'blue'} texto={dayjs(cellEstadoReporteVentaCorporativa(row.original).fecha_registro).format("LLL") || ''} />
      )
    },
    {
      accessorKey: 'fecha_creacion',
      header: 'Fecha Creación',
      enableColumnFilter: false,
      Cell: ({ row }) => (
        formatDate(row.original.fecha_creacion||"")
      )
    },
    {
      accessorKey: 'fecha_modificacion',
      header: 'Fecha Modificación',
      enableColumnFilter: false,
      Cell: ({ row }) => dayjs(row.original?.trazo_corporativo_data?.[(row.original?.trazo_corporativo_data?.length||1)-1]?.fecha_modificacion).format("LLL") || "N/A"
    },
  ], [infoCoordendas, setOpen, setDocumentsArray]);

  return {
    columnsSeguimientoCoste
  }
}
